/**
 * Bell-icon header control + notifications dropdown (#571 PR 3,
 * ADR 0012).
 *
 * Click the bell to open a dropdown of recent feed entries; the
 * dropdown lists newest-first with title / body / relative time +
 * a per-row deep-link on click. Closes on outside-click and on
 * navigation.
 *
 * The badge shows unread count, capped visually at "9+" so the
 * pill doesn't grow indefinitely. Hidden when count is 0.
 */
import { useState, useRef, useEffect } from 'react'
import { Bell, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui'
import { useNotifications } from '@/hooks/useNotifications'
import type { NotificationItem } from '@/lib/api'

export function NotificationsBell() {
  const navigate = useNavigate()
  const { unreadCount, items, loadingFeed, refreshFeed, markRead, markAllRead, dismiss, clearAll } =
    useNotifications()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Refresh the feed when the dropdown opens — the unreadCount poll
  // keeps the badge live, but the feed itself only fetches on demand.
  useEffect(() => {
    if (open) void refreshFeed()
  }, [open, refreshFeed])

  // Outside-click close. Same pattern as the app/variant dropdown
  // in Header.tsx.
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const handleItemClick = async (item: NotificationItem) => {
    if (!item.read) {
      // Don't block navigation on the mark-read round-trip — fire
      // and forget; the optimistic update inside the hook makes the
      // UI feel instant.
      void markRead(item._id)
    }
    setOpen(false)
    if (item.deepLinkPath) {
      navigate(item.deepLinkPath)
    }
  }

  const badgeLabel = unreadCount > 9 ? '9+' : String(unreadCount)

  return (
    <div className="relative" ref={containerRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(prev => !prev)}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center pointer-events-none"
            aria-hidden="true"
          >
            {badgeLabel}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute top-full right-0 mt-1 w-[360px] max-h-[480px] overflow-y-auto bg-popover border rounded-lg shadow-lg z-50">
          <div className="sticky top-0 bg-popover border-b px-3 py-2 flex items-center justify-between">
            <span className="text-sm font-semibold">Notifications</span>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  onClick={() => void markAllRead()}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Mark all read
                </button>
              )}
              {items.length > 0 && (
                <button
                  onClick={() => void clearAll()}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {loadingFeed && items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              You're all caught up.
            </div>
          ) : (
            <ul className="divide-y">
              {items.map(item => (
                // Flex row: the clickable content area and the dismiss
                // "×" are siblings — a <button> cannot legally nest
                // inside another <button>.
                <li
                  key={item._id}
                  className={`flex items-start hover:bg-muted/50 transition-colors ${
                    item.read ? '' : 'bg-primary/5'
                  }`}
                >
                  <button
                    onClick={() => void handleItemClick(item)}
                    className="flex-1 min-w-0 text-left px-3 py-2.5"
                  >
                    <div className="flex items-start gap-2">
                      {!item.read && (
                        <span
                          className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0"
                          aria-label="Unread"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{item.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{item.body}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">
                          {formatRelative(item.createdAt)}
                        </div>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      // Don't let the click bubble to the row content's
                      // mark-read + navigate handler — dismiss only.
                      e.stopPropagation()
                      void dismiss(item._id)
                    }}
                    className="shrink-0 px-2 py-2.5 text-muted-foreground hover:text-foreground transition-colors"
                    title="Dismiss"
                    aria-label="Dismiss notification"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

/** Compact relative time for the dropdown rows. Same family as
 * "5m ago" / "2h ago" / "3d ago". Falls back to absolute date past
 * 30 days when relative loses meaning. */
function formatRelative(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const seconds = Math.max(1, Math.round((now - then) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}
